import { LIBERTY_USER_MARKERS } from "constants/LibertyMutualConstants";
import { SECTION } from "constants/SettingsConstants";
import type { FileType } from "types/DataTypes";

type ServiceTypeAndId = {
  id?: number;
  type?: string;
};

/*
MSA and Amended Review are the same thing (MSA), just different "type"
LCP and MCP are the same thing (Cost Projection), just different "type"
this function is used to get the service (MSA or Cost Projection) field for the query and mutation */
export const getServiceField = (
  service?: ServiceTypeAndId,
  isQuery = false,
  reviewType?: "MEDICAL" | "LEGAL",
) => {
  // early return if service is undefined
  if (!service) {
    return {};
  }

  let baseField: Record<string, unknown> = {};

  if (isQuery) {
    // for query params (GET requests)
    switch (service.type) {
      case SECTION.MCP:
      case SECTION.LCP:
        baseField = { cost_projection_id: service.id };
        break;
      case SECTION.AMENDED_REVIEW:
        baseField = { msa_id: service.id };
        break;
      case SECTION.MSA_111:
        baseField = { msa_id: service.id };
        break;
      case SECTION.DOCUMENT_MANAGEMENT:
        baseField = { document_management_file_id: service.id };
        break;
      default:
        baseField = { msa_id: service.id };
    }

    // add review_type to query params if provided
    if (reviewType) {
      baseField.review_type = reviewType;
    }

    return baseField;
  }

  // for mutation payloads (POST, PATCH, DELETE)
  switch (service.type) {
    case SECTION.MCP:
    case SECTION.LCP:
      baseField = { cost_projection: service.id };
      break;
    case SECTION.AMENDED_REVIEW:
    case SECTION.MSA_111:
      baseField = { msa: service.id };
      break;
    case SECTION.DOCUMENT_MANAGEMENT:
      baseField = { document_management_file: service.id };
      break;
    default:
      baseField = { msa: service.id };
  }

  // add review_type to mutation payload if provided
  if (reviewType) {
    baseField.review_type = reviewType;
  }

  return baseField;
};

// Single shared "is this a Liberty Mutual file" check -- do not repeat this
// condition inline elsewhere (mirrors File.is_liberty_mutual in app-server's
// operations/models/file.py, including the creator fallback). organization
// === 1 is Allan Koba's own organization (the only tenant Liberty Mutual is a
// client of), not an ID belonging to Liberty Mutual itself.
export const isLibertyMutualFile = (file?: FileType | null): boolean => {
  if (file?.organization !== 1) {
    return false;
  }
  // client is typed non-nullable but is null on every intake-created file
  // until Operations assigns one, hence the optional chaining.
  if (file.client?.name?.includes("Liberty Mutual")) {
    return true;
  }
  // Intake never assigns a Client, so a Liberty referral is recognized by its
  // creator until then. FileSerializer.get_creator returns {id, email}; other
  // payloads carry a bare user id, which tells us nothing either way.
  const creatorEmail =
    typeof file.creator === "object" ? (file.creator?.email ?? "") : "";
  return LIBERTY_USER_MARKERS.some((marker) =>
    creatorEmail.toLowerCase().includes(marker),
  );
};

export type CopyMedicalRecordsServiceType = "msa" | "cost_projection";

export const getCopyMedicalRecordsServiceType = (
  service?: ServiceTypeAndId,
): CopyMedicalRecordsServiceType | null => {
  if (!service) {
    return null;
  }

  switch (service.type) {
    case SECTION.MCP:
    case SECTION.LCP:
      return "cost_projection";
    case SECTION.DOCUMENT_MANAGEMENT:
      return null;
    default:
      return "msa";
  }
};
